import React, { useState, useEffect, useRef } from 'react';
import { X, MapPin, Search, Save } from 'lucide-react';

// Google Maps API configuration
const GOOGLE_MAPS_API_KEY = 'AIzaSyBSTQdQL17yVci86qzot_IPHKuJGZ8FQiA';

// Load Google Maps API
const loadGoogleMapsAPI = () => {
  return new Promise((resolve, reject) => {
    if (window.google && window.google.maps) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=geometry`;
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

const LocationSelectionModal = ({ isOpen, onClose, onSave, initialLocation = null }) => {
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [map, setMap] = useState(null);
  const [geocoder, setGeocoder] = useState(null);
  const [marker, setMarker] = useState(null);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  
  // Location state
  const [selectedLocation, setSelectedLocation] = useState(initialLocation);
  const [searchAddress, setSearchAddress] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);

  // Load Google Maps API when modal opens
  useEffect(() => {
    if (isOpen && !googleMapsLoaded) {
      loadGoogleMapsAPI()
        .then(() => {
          setGoogleMapsLoaded(true);
        })
        .catch((error) => {
          console.error('Failed to load Google Maps API:', error);
        });
    }
  }, [isOpen, googleMapsLoaded]);

  // Clean up map when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Clean up map state when modal closes
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
      if (marker) {
        marker.setMap(null);
      }
      setMap(null);
      setGeocoder(null);
      setMarker(null);
      setSelectedLocation(null);
      setSearchAddress('');
      setSearchError(null);
    }
  }, [isOpen, marker]);

  // Initialize Google Maps when modal opens
  useEffect(() => {
    if (isOpen && googleMapsLoaded && mapRef.current) {
      try {
        // Determine initial center and zoom
        let initialCenter = { lat: 49.4521, lng: 11.0767 }; // Default center (Germany)
        let initialZoom = 8;

        // If we have an initial location, use that as center
        if (initialLocation?.coordinates) {
          initialCenter = initialLocation.coordinates;
          initialZoom = 12;
        }

        const mapInstance = new window.google.maps.Map(mapRef.current, {
          zoom: initialZoom,
          center: initialCenter,
          mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        });

        const geocoderInstance = new window.google.maps.Geocoder();

        // Add click listener for placing marker
        mapInstance.addListener('click', (event) => {
          const latLng = event.latLng;
          placeMarker(latLng, mapInstance, geocoderInstance);
        });

        setMap(mapInstance);
        setGeocoder(geocoderInstance);

        // If there's an initial location, place marker
        if (initialLocation?.coordinates) {
          placeMarker(initialLocation.coordinates, mapInstance, geocoderInstance, false);
        }
      } catch (error) {
        console.error('Error initializing Google Maps:', error);
      }
    }
  }, [isOpen, googleMapsLoaded, initialLocation]);

  // Place marker on map
  const placeMarker = (latLng, mapInstance = map, geocoderInstance = geocoder, shouldGeocode = true) => {
    // Remove existing marker
    if (markerRef.current) {
      markerRef.current.setMap(null);
      markerRef.current = null;
    }
    if (marker) {
      marker.setMap(null);
    }

    // Create new marker
    const newMarker = new window.google.maps.Marker({
      position: latLng,
      map: mapInstance,
      title: 'Projektstandort',
      draggable: true,
      icon: {
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
          <svg width="24" height="32" viewBox="0 0 24 32" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 20 12 20s12-11 12-20c0-6.627-5.373-12-12-12z" fill="#16a34a" stroke="white" stroke-width="2"/>
            <circle cx="12" cy="12" r="4" fill="white"/>
          </svg>
        `),
        scaledSize: new window.google.maps.Size(24, 32),
        anchor: new window.google.maps.Point(12, 32),
      },
    });

    // Add drag listener
    newMarker.addListener('dragend', (event) => {
      placeMarker(event.latLng, mapInstance, geocoderInstance);
    });

    markerRef.current = newMarker;
    setMarker(newMarker);

    // Geocode the location to get address
    if (shouldGeocode && geocoderInstance) {
      geocoderInstance.geocode({ location: latLng }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const address = results[0].formatted_address;
          setSelectedLocation({
            coordinates: latLng,
            address: address,
            lat: latLng.lat(),
            lng: latLng.lng()
          });
        } else {
          // If geocoding fails, still save coordinates
          setSelectedLocation({
            coordinates: latLng,
            address: `${latLng.lat().toFixed(6)}, ${latLng.lng().toFixed(6)}`,
            lat: latLng.lat(),
            lng: latLng.lng()
          });
        }
      });
    }
  };

  // Handle address search
  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchAddress.trim() || !geocoder || isSearching) return;

    setIsSearching(true);
    setSearchError(null);

    try {
      geocoder.geocode({ address: searchAddress.trim() }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const location = results[0].geometry.location;
          const address = results[0].formatted_address;
          
          // Center map on found location
          map.setCenter(location);
          map.setZoom(15);
          
          // Place marker
          placeMarker(location, map, geocoder, false);
          
          setSelectedLocation({
            coordinates: location,
            address: address,
            lat: location.lat(),
            lng: location.lng()
          });
        } else {
          setSearchError('Adresse nicht gefunden. Bitte versuchen Sie einen anderen Suchbegriff.');
        }
        setIsSearching(false);
      });
    } catch (error) {
      console.error('Geocoding error:', error);
      setSearchError('Adresssuche fehlgeschlagen');
      setIsSearching(false);
    }
  };

  const handleSave = () => {
    if (selectedLocation) {
      onSave(selectedLocation);
      onClose();
    }
  };

  const handleCancel = () => {
    setSelectedLocation(initialLocation);
    setSearchAddress('');
    setSearchError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-6 border-b flex-shrink-0">
          <h2 className="text-xl font-semibold">Projektstandort entweder eingeben oder direkt auf der Karte auswählen</h2>
          <button
            onClick={handleCancel}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 flex flex-col min-h-0">
          {/* Search Bar */}
          <div className="p-4 border-b bg-gray-50 flex-shrink-0">
            <form onSubmit={handleSearch} className="flex gap-2">
              <input
                type="text"
                value={searchAddress}
                onChange={(e) => setSearchAddress(e.target.value)}
                placeholder="Adresse oder Koordinaten eingeben"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                disabled={isSearching}
              />
              <button
                type="submit"
                disabled={isSearching || !searchAddress.trim()}
                className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSearching ? 'Suche...' : 'Suchen'}
              </button>
            </form>

            {searchError && (
              <div className="mt-2 text-sm text-red-600">{searchError}</div>
            )}
          </div>

          {/* Map Container */}
          <div className="flex-1 relative min-h-0" style={{ minHeight: '300px' }}>
            <div
              ref={mapRef}
              className="w-full h-full cursor-crosshair"
              style={{ minHeight: '300px' }}
            />
          </div>

          {/* Selected Location Display */}
          {selectedLocation && (
            <div className="p-3 border-t bg-gray-50 flex-shrink-0">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <MapPin className="h-4 w-4 flex-shrink-0" />
                <span className="truncate">Ausgewählter Standort: {selectedLocation.address}</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-4 p-6 border-t flex-shrink-0">
          <button
            onClick={handleCancel}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            disabled={!selectedLocation}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Standort auswählen
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocationSelectionModal;
